/**
 * ThresholdAccordion Component
 *
 * This component renders two accordion items for managing threshold values for a list of sensors.
 *
 * Props:
 * - sensorsData: Data about the sensors.
 * - handleThresholdSaveClick: Function to handle saving the threshold values.
 * - handleThresholdAdditiveSaveClick: Function to handle saving the additive threshold values.
 * - onAdd: Function to handle adding a new threshold.
 *
 * State:
 * - minThreshold: Minimum threshold value.
 * - maxThreshold: Maximum threshold value.
 * - minAdditive: Minimum additive value for the threshold.
 * - maxAdditive: Maximum additive value for the threshold.
 * - isAccordion1Open: Boolean to manage the visibility of the first accordion item.
 * - isAccordion2Open: Boolean to manage the visibility of the second accordion item.
 *
 * The component allows users to input minimum and maximum threshold values directly or adjust them
 * additively. It provides a clear interface for managing these values across all sensors listed.
 */

import React, { useState } from "react";
import { Accordion, Button, Form } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useTranslation from "../../../../hooks/useTranslation";
import "../ContainerMoreInfoPage.scss";
import ConfirmationModal from "../../../Shared/ConfirmationModal/ConfirmationModal";

const ThresholdAccordion = ({
  sensorsData,
  handleThresholdSaveClick,
  handleThresholdAdditiveSaveClick,
  onAdd,
  sensorMinThreshold,
  sensorMaxThreshold,
}) => {
  const { t } = useTranslation("locationsPage");
  const [minThreshold, setMinThreshold] = useState("");
  const [maxThreshold, setMaxThreshold] = useState("");
  const [minAdditive, setMinAdditive] = useState(0);
  const [maxAdditive, setMaxAdditive] = useState(0);

  const [isAccordion1Open, setIsAccordion1Open] = useState(true);
  const [isAccordion2Open, setIsAccordion2Open] = useState(false);

  // NEW: State to control the confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdditiveDeleteModal, setShowAdditiveDeleteModal] = useState(false);

  const handleAccordion1Click = () => {
    setIsAccordion1Open(!isAccordion1Open);
  };

  const handleAccordion2Click = () => {
    setIsAccordion2Open(!isAccordion2Open);
  };

  // Clears the input fields without affecting the database
  const handleFormClearClick = () => {
    setMinThreshold("");
    setMaxThreshold("");
  };

  const handleAdditiveFormClearClick = () => {
    setMinAdditive(0);
    setMaxAdditive(0);
  };

  const handleConfirmDeleteAllAdditive = () => {
    setShowAdditiveDeleteModal(false);
    handleThresholdAdditiveSaveClick(null, null, true);
  };

  // NEW: Triggered when the user clicks "Yes" in the modal
  const handleConfirmDeleteAll = () => {
    setShowDeleteModal(false);
    // Send nulls to trigger the backend deletion
    handleThresholdSaveClick(null, null, true);
  };

  return (
    <div className="threshold-accordion-container">
      <ToastContainer />
      <Accordion className="threshold-accordion">
        <Accordion.Item eventKey="0" className="accordion-1">
          <Accordion.Header
            className={`accordion-1-header ${
              isAccordion1Open ? "accordion-1-open-header" : ""
            }`}
            onClick={handleAccordion1Click}
          >
            {t("thresholdvalues_modify_absolute_for_every_sensor")}
          </Accordion.Header>
          <Accordion.Body className="accordion-1-body">
            <Form>
              <Form.Group controlId="formMinThreshold">
                <Form.Label>{t("minValue")}</Form.Label>
                <Form.Control
                  type="number"
                  min={sensorMinThreshold}
                  max={sensorMaxThreshold}
                  value={minThreshold}
                  onChange={(e) => {
                    if (!isNaN(e.target.value) || e.target.value === "") {
                      setMinThreshold(e.target.value);
                    }
                  }}
                />
              </Form.Group>

              <Form.Group controlId="formMaxThreshold">
                <Form.Label>{t("maxValue")}</Form.Label>
                <Form.Control
                  type="number"
                  min={sensorMinThreshold}
                  max={sensorMaxThreshold}
                  value={maxThreshold}
                  onChange={(e) => {
                    if (!isNaN(e.target.value) || e.target.value === "") {
                      setMaxThreshold(e.target.value);
                    }
                  }}
                />
              </Form.Group>

              <div
                className="accordion-button-wrapper"
                style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
              >
                <Button
                  variant="primary"
                  onClick={() =>
                    handleThresholdSaveClick(minThreshold, maxThreshold, false)
                  }
                  className="accordion-submit-button"
                >
                  {t("save")}
                </Button>

                <Button
                  variant="secondary"
                  onClick={handleFormClearClick}
                  className="accordion-submit-button accordion-clear-button"
                >
                  {t("clear") || "Mezők ürítése"}
                </Button>

                {/* Opens the confirmation modal instead of deleting immediately */}
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                  className="accordion-submit-button accordion-delete-button"
                >
                  {t("delete_all") || "Összes törlése"}
                </Button>
              </div>
            </Form>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          <Accordion.Header onClick={handleAccordion2Click}>
            {t("thresholdvalues_modify_additive_for_every_sensor")}
          </Accordion.Header>
          <Accordion.Body>
            <Form>
              <Form.Group controlId="formMinAdditive">
                <Form.Label>{t("modify_min_threshold_with")}:</Form.Label>
                <Form.Control
                  type="number"
                  value={minAdditive}
                  onChange={(e) => {
                    if (!isNaN(e.target.value) || e.target.value === "") {
                      setMinAdditive(e.target.value);
                    }
                  }}
                />
              </Form.Group>

              <Form.Group controlId="formMaxAdditive">
                <Form.Label>{t("modify_max_threshold_with")}:</Form.Label>
                <Form.Control
                  type="number"
                  value={maxAdditive}
                  onChange={(e) => {
                    if (!isNaN(e.target.value) || e.target.value === "") {
                      setMaxAdditive(e.target.value);
                    }
                  }}
                />
              </Form.Group>
              <div
                className="accordion-button-wrapper"
                style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
              >
                <Button
                  variant="primary"
                  onClick={() =>
                    handleThresholdAdditiveSaveClick(
                      minAdditive,
                      maxAdditive,
                      false,
                    )
                  }
                  className="accordion-submit-button"
                >
                  {t("save")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleAdditiveFormClearClick}
                  className="accordion-submit-button accordion-clear-button"
                >
                  {t("clear") || "Mezők ürítése"}
                </Button>
                {/* NEW Delete All Button */}
                <Button
                  variant="danger"
                  onClick={() => setShowAdditiveDeleteModal(true)}
                  className="accordion-submit-button accordion-delete-button red!"
                >
                  {t("delete_all") || "Összes törlése"}
                </Button>
              </div>
            </Form>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* NEW: Confirmation Modal Instance */}
      <ConfirmationModal
        isVisible={showDeleteModal}
        onConfirm={handleConfirmDeleteAll}
        onCancel={() => setShowDeleteModal(false)}
        message={
          t("confirm_delete_all_thresholds") ||
          "Biztosan törli az összes beállított küszöbértéket ebben a tárolóban?"
        }
      />
      <ConfirmationModal
        isVisible={showAdditiveDeleteModal}
        onConfirm={handleConfirmDeleteAllAdditive}
        onCancel={() => setShowAdditiveDeleteModal(false)}
        message={
          t("confirm_delete_all_thresholds") ||
          "Biztosan törli az összes beállított küszöbértéket ebben a tárolóban?"
        }
      />
    </div>
  );
};

export default ThresholdAccordion;
